import React, { useMemo } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { userMatchesField, cellText, getStatusBadge, getPriorityBadge, formatDate, calculatePerformanceRating } from "../../utils/helpers";


export default function EmployeeDashboard({ onNavigate }) {
  const { userRecords, adminRecords, loading, lastFetched } = useData();
  const { user } = useAuth();

  const myTasks = useMemo(() =>
    userRecords.filter(r => r.user_id === user?.userId),
    [userRecords, user]
  );

  const stats = useMemo(() => {
    const total = myTasks.length;
    const done = myTasks.filter(r => !!r.fields?.["My Completion"]).length;
    const pending = myTasks.filter(r => !r.fields?.["My Completion"]);
    
    const overdue = pending.filter(r => {
      const s = cellText(r.fields?.["Overall Status"]).toLowerCase();
      return s === "🔴 overdue" || (s.includes("overdue") && !s.includes("done"));
    }).length;

    const inProgress = pending.filter(r => {
      const s = cellText(r.fields?.["Overall Status"]).toLowerCase();
      return s.includes("progress") || s.includes("🟡");
    }).length;

    const notStarted = pending.filter(r => {
      const s = cellText(r.fields?.["Overall Status"]).toLowerCase();
      return s.includes("not started") || s.includes("⬜") || s === "";
    }).length;

    return {
      total,
      done,
      overdue,
      inProgress,
      notStarted,
      created:    adminRecords.filter(r => {
        const creator = r.fields?.["Task Creator"] || r.fields?.["Assigned By"];
        return userMatchesField(creator, user);
      }).length,
      monitoring: adminRecords.filter(r => userMatchesField(r.fields?.["Supervisor"], user)).length,
    };
  }, [myTasks, adminRecords, user]);

  const upcoming = useMemo(() =>
    myTasks.filter(r => !cellText(r.fields?.["Overall Status"]).toLowerCase().includes("done"))
      .sort((a,b) => (a.fields?.["End Date"]||0) - (b.fields?.["End Date"]||0)).slice(0,5),
    [myTasks]
  );

  if (loading && userRecords.length === 0) return <div className="loading-box"><div className="spinner"/><span>Loading your tasks...</span></div>;

  const STAT_CARDS = [
    { label:"My Tasks",     value:stats.total,      color:"#6366F1", icon:"📋", page:"all-tasks"  },
    { label:"Completed",    value:stats.done,       color:"#10B981", icon:"✅", page:"completed"  },
    { label:"In Progress",  value:stats.inProgress, color:"#F59E0B", icon:"🟡", page:"pending"    },
    { label:"Overdue",      value:stats.overdue,    color:"#EF4444", icon:"🔴", page:"overdue"    },
    { label:"Not Started",  value:stats.notStarted, color:"#64748B", icon:"⬜", page:"pending"    },
    { label:"I Created",    value:stats.created,    color:"#8B5CF6", icon:"📝", page:"created"    },
    { label:"Monitoring",   value:stats.monitoring, color:"#3B82F6", icon:"👁️", page:"monitor"    },
    { label:"Rating",       value:calculatePerformanceRating(myTasks).toFixed(2), color:"#ec4899", icon:"🏆", page:"all-tasks" },
  ];

  return (
    <div className="fade-in">
      <div className="welcome-banner">
        <div className="welcome-text">
          <h2>Hey, {user?.name?.split(" ")[0]}! 👋</h2>
          <p>You have {stats.total} task{stats.total !== 1 ? "s" : ""} assigned {lastFetched && `· Updated ${lastFetched}`}</p>
        </div>
        <div className="welcome-emoji">🎯</div>
      </div>

      {stats.overdue > 0 && (
        <div className="overdue-banner">
          🚨 You have <strong style={{ margin:"0 4px" }}>{stats.overdue}</strong> overdue task{stats.overdue>1?"s":""}!
          <button className="btn btn-danger btn-sm" style={{ marginLeft:"auto" }} onClick={() => onNavigate("overdue")}>View →</button>
        </div>
      )}

      <div className="stats-grid">
        {STAT_CARDS.map(s => (
          <div key={s.label} className="stat-card" style={{ borderTop:`3px solid ${s.color}`, cursor:"pointer" }} onClick={() => onNavigate(s.page)}>
            <div className="stat-card-icon" style={{ background:`${s.color}15` }}>{s.icon}</div>
            <div className="stat-card-value" style={{ color:s.color }}>{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div className="stat-card-sub">Click to view →</div>
          </div>
        ))}
      </div>


      <div className="card">
        <div className="card-header">
          <span className="card-title">⏰ Upcoming Due Tasks</span>
          <button className="btn btn-secondary btn-sm" onClick={() => onNavigate("all-tasks")}>View All →</button>
        </div>
        {upcoming.length === 0 ? (
          <div className="empty-box"><div className="empty-icon">🎉</div><p>No upcoming tasks — you're all clear!</p></div>
        ) : (
          <div className="table-wrapper incard-table">
            <table>
              <thead><tr><th>Task ID</th><th>Task Name</th><th>Status</th><th>Priority</th><th>Due Date</th></tr></thead>
              <tbody>
                {upcoming.map((row,i) => {
                  const f = row.fields || {};
                  const { cls, label } = getStatusBadge(cellText(f["Overall Status"]));
                  const isOD = label?.toLowerCase().includes("overdue");
                  return (
                    <tr key={i}>
                      <td style={{ color:"var(--primary)", fontWeight:700, fontSize:12 }}>{cellText(f["Task ID"])||"—"}</td>
                      <td style={{ fontWeight:500 }}>{cellText(f["Task Name"])||"—"}</td>
                      <td><span className={`badge ${cls}`}>{label||"—"}</span></td>
                      <td>
                        {(() => {
                          const { bg, color, label } = getPriorityBadge(cellText(f["Priority"]));
                          return <span className="badge" style={{ background:bg, color:color }}>{label}</span>;
                        })()}
                      </td>
                      <td style={{ fontSize:12, color:isOD?"var(--danger)":"var(--gray-500)", fontWeight:isOD?700:400 }}>
                        {formatDate(f["End Date"])}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
